import html2canvas from 'html2canvas'

import { getSectionsScreenshots } from '../../../features/components/PdfGeneration/generatePdfReport'

import { gridCharts, fullWidthCharts, sectionIds, EXPORT_CHART_ICON_ID, pdfExportComponentKeys } from './constants'

// Helper to expand scrollable elements ONLY within specific sections for PDF capture
// This prevents expanding visible elements - only expands the hidden export copies
const expandScrollableElements = sections => {
  const scrollableElements = []

  // Only expand scrollable elements within the sections we're capturing
  sections.forEach(section => {
    if (!section) return

    // Find all elements with overflow: auto or scroll within this specific section
    const elementsInSection = section.querySelectorAll('*')

    elementsInSection.forEach(element => {
      const computedStyle = window.getComputedStyle(element)
      const overflowY = computedStyle.overflowY
      const overflowX = computedStyle.overflowX

      // Check if element has scroll overflow
      if (overflowY === 'auto' || overflowY === 'scroll' || overflowX === 'auto' || overflowX === 'scroll') {
        // Store original styles
        scrollableElements.push({
          element,
          originalHeight: element.style.height,
          originalMaxHeight: element.style.maxHeight,
          originalOverflowY: element.style.overflowY,
          originalOverflowX: element.style.overflowX
        })

        // Temporarily expand to show all content
        element.style.height = 'auto'
        element.style.maxHeight = 'none'
        element.style.overflowY = 'visible'
        element.style.overflowX = 'visible'
      }
    })
  })

  return { scrollableElements }
}

// Helper to restore scrollable elements after PDF capture
const restoreScrollableElements = scrollableElements => {
  scrollableElements.forEach(({ element, originalHeight, originalMaxHeight, originalOverflowY, originalOverflowX }) => {
    element.style.height = originalHeight
    element.style.maxHeight = originalMaxHeight
    element.style.overflowY = originalOverflowY
    element.style.overflowX = originalOverflowX
  })
}

// Helper to capture individual chart screenshots and group them for column layout
const captureGridChartsAsColumns = async (charts, columnGap) => {
  const content = []
  const A3_WIDTH = 842
  const PAGE_MARGIN_X = 20
  const availablePageWidth = A3_WIDTH - PAGE_MARGIN_X * 2
  const columnWidth = (availablePageWidth - columnGap) / 2

  // Group charts by 2 for column layout
  for (let i = 0; i < charts.length; i += 2) {
    const chartPair = charts.slice(i, i + 2)
    const columns = []

    for (const chart of chartPair) {
      if (chart) {
        // Capture each chart individually at full width
        const canvas = await html2canvas(chart, {
          backgroundColor: null,
          useCORS: true // Required for loading SDG icon images in MappingOfImpactToSdgs
        })

        const dataUrl = canvas.toDataURL()
        columns.push({
          image: dataUrl,
          width: columnWidth
        })
      }
    }

    // Add column layout to content
    if (columns.length > 0) {
      content.push({
        columns,
        columnGap,
        marginTop: columnGap // Use same gap for vertical spacing
      })
    }
  }

  return content
}

export const getReportPDFSections = async (brandColor, waitForComponents) => {
  // Gap between sections/charts - matches ReportContent/styles.js
  const gap = 40

  // Wait for dynamic components to finish rendering
  // Components will signal readiness via the PDF export context
  if (waitForComponents) {
    const componentsToWait = []

    // Check if SocialValueByStakeholders is rendered (moves to full-width during PDF)
    const hasStakeholdersChart = document.querySelector(`#${gridCharts.sroiSocialValueStakeholders}`)
    if (hasStakeholdersChart) {
      componentsToWait.push(pdfExportComponentKeys.stakeholdersChart)
    }

    // Check if SroiInfoPanels export copy exists
    const hasSroiInfoPanelsExport = document.querySelector(`#${sectionIds.sroiInfoPanels}-export`)
    if (hasSroiInfoPanelsExport) {
      componentsToWait.push(pdfExportComponentKeys.sroiInfoPanels)
    }

    if (componentsToWait.length > 0) {
      await waitForComponents(componentsToWait)
    }
  }

  const sections = []

  // Get header sections
  // Use the -export copy if available (rendered at bottom of page during PDF generation)
  // This copy will be expanded while the original stays visible and unchanged
  const sroiInfoPanels =
    document.querySelector(`#${sectionIds.sroiInfoPanels}-export`) ||
    document.querySelector(`#${sectionIds.sroiInfoPanels}`)
  const sroiFilters = document.querySelector(`#${sectionIds.sroiFilters}`)
  const totalsSummaryPanels = document.querySelector(`#${sectionIds.totalsSummaryPanels}`)

  // Add header sections
  if (sroiInfoPanels) {
    sections.push(sroiInfoPanels)
  }

  if (sroiFilters) {
    sections.push(sroiFilters)
  }

  if (totalsSummaryPanels) {
    sections.push(totalsSummaryPanels)
  }

  // Hide any instances of SocialValueByStakeholders in grid container
  // This ensures only the full-width instance is captured during PDF export
  const gridContainer = document.querySelector('#sroi-charts-container')
  const allStakeholdersCharts = document.querySelectorAll(`#${gridCharts.sroiSocialValueStakeholders}`)
  const hiddenElements = []

  allStakeholdersCharts.forEach(chart => {
    const isInGrid = gridContainer?.contains(chart)
    if (isInGrid) {
      const originalDisplay = chart.style.display
      const originalVisibility = chart.style.visibility
      chart.style.display = 'none'
      chart.style.visibility = 'hidden'
      hiddenElements.push({ chart, originalDisplay, originalVisibility })
    }
  })

  // Get individual charts by their IDs
  // Skip sroiSocialValueStakeholders - it will be added to full-width section during PDF export
  const charts = Object.values(gridCharts)
    .filter(id => id !== gridCharts.sroiSocialValueStakeholders)
    .map(id => document.querySelector(`#${id}`))
    .filter(Boolean)

  // Expand scrollable elements in header sections (includes SroiInfoPanels)
  const { scrollableElements: headerScrollableElements } = expandScrollableElements(sections)

  // Capture header sections (info panels, filters, summary)
  const headerContent = await getSectionsScreenshots(sections, gap)

  // Restore scrollable elements for header sections
  restoreScrollableElements(headerScrollableElements)

  // Hide download icons for grid charts
  const gridChartElements = []
  charts.forEach(chart => {
    const downloadIcon = chart.querySelector(`#${EXPORT_CHART_ICON_ID}`)
    if (downloadIcon) {
      downloadIcon.classList.add('pdf-hidden')
      gridChartElements.push({ chart, downloadIcon })
    }
  })

  // Expand scrollable elements in grid charts before capturing
  const { scrollableElements: gridScrollableElements } = expandScrollableElements(charts)

  // Capture grid charts individually and group them into 2-column layout
  const gridChartsContent = await captureGridChartsAsColumns(charts, gap)

  // Restore scrollable elements for grid charts
  restoreScrollableElements(gridScrollableElements)

  // Collect full-width chart sections
  const fullWidthSections = []
  const fullWidthChartElements = []

  // First, add SocialValueByStakeholders if it exists (moved to full-width during PDF export)
  const fullWidthContainer = document.querySelector('#sroi-full-width-container')
  const stakeholdersChart = fullWidthContainer?.querySelector(`#${gridCharts.sroiSocialValueStakeholders}`)

  if (stakeholdersChart) {
    const downloadIcon = stakeholdersChart.querySelector(`#${EXPORT_CHART_ICON_ID}`)
    if (downloadIcon) {
      downloadIcon.classList.add('pdf-hidden')
      fullWidthChartElements.push({ chart: stakeholdersChart, downloadIcon })
    }
    fullWidthSections.push(stakeholdersChart)
  }

  // Then add other full-width charts
  Object.values(fullWidthCharts).forEach(id => {
    const chart = document.querySelector(`#${id}`)
    if (chart) {
      // Temporarily hide download icon for full-width charts
      const downloadIcon = chart.querySelector(`#${EXPORT_CHART_ICON_ID}`)
      if (downloadIcon) {
        downloadIcon.classList.add('pdf-hidden')
        // Store reference to restore later
        fullWidthChartElements.push({ chart, downloadIcon })
      }
      fullWidthSections.push(chart)
    }
  })

  // Expand scrollable elements in full-width sections
  const { scrollableElements: fullWidthScrollableElements } = expandScrollableElements(fullWidthSections)

  // Capture full-width charts (don't skip first margin - these come after grid charts)
  const fullWidthContent = await getSectionsScreenshots(fullWidthSections, gap, false)

  // Restore scrollable elements for full-width charts
  restoreScrollableElements(fullWidthScrollableElements)

  // Combine all content: headers + grid charts (in columns) + full-width charts
  const content = [...headerContent, ...gridChartsContent, ...fullWidthContent]

  // Restore download icons for grid charts
  gridChartElements.forEach(({ downloadIcon }) => {
    if (downloadIcon) {
      downloadIcon.classList.remove('pdf-hidden')
    }
  })

  // Restore download icons for full-width charts
  fullWidthChartElements.forEach(({ downloadIcon }) => {
    if (downloadIcon) {
      downloadIcon.classList.remove('pdf-hidden')
    }
  })

  // Restore all hidden SocialValueByStakeholders instances
  hiddenElements.forEach(({ chart, originalDisplay, originalVisibility }) => {
    chart.style.display = originalDisplay
    chart.style.visibility = originalVisibility
  })

  return content
}
